import React, { useEffect, useRef, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import ConnectedAccounts from "@/pages/settings/pages/ConnectedAccounts";
import { SETTINGS_REFERRER_KEY } from "@/constants";
import useAccount from "@/entities/accounts/hooks";
import { useUsersMe } from "@/entities/users/hooks";
import { cn } from "@/utils/utils";
import StoreInitializer from "@/services/store/Initializer";
import { useFeatureFlag, useFeatureFlags } from "@/services/features/hook";
import AuthRedirect, { AuthRedirectProps } from "@/services/auth/AuthRedirect";

import { GeneralSettings } from "@/pages/settings/pages/GeneralSettings";
import { UserManagementSettings } from "@/pages/settings/pages/UserManagementSettings";
import SettingsSideBar from "@/pages/settings/components/settingsNav/SettingsSideBar";
import { getSettingsPageNameFromPath } from "@/pages/settings/components/settingsNav/utils";
import Toaster from "@/components/ui/toast";
import { DealsSettings } from "@/pages/settings/pages/DealsSettings";
import { CompaniesSettings } from "@/pages/settings/pages/CompaniesSettings";
import { PeopleSettings } from "@/pages/settings/pages/PeopleSettings";
import { LeadSettings } from "@/pages/settings/pages/LeadSettings";
import StageDetailedView from "@/pages/settings/components/dealsSettings/StageStudio/StageDetailedView";
import WorkflowDetailedView from "@/pages/settings/components/dealsSettings/StageStudio/workflow/WorkflowDetailedView";
import { FeatureFlags } from "@/services/features/types";
import { ProductsSettings } from "@/pages/settings/pages/products-v2/ProductsSettings";
import { PersonalApiKey } from "@/pages/settings/pages/PersonalApiKey";
import { ContractsSettings } from "@/pages/settings/pages/ContractsSettings";
import OnboardingStageDetailedView from "@/pages/settings/components/contractsSettings/onboardingStudio/OnboardingStageDetailedView";
import OnboardingWorkflowDetailedView from "@/pages/settings/components/contractsSettings/onboardingStudio/workflow/OnboardingWorkflowDetailedView";
import { PartnersSettings } from "@/pages/settings/pages/PartnersSettings";
import { useCanAccessAdminSettings } from "@/pages/settings/hooks";
import NotFoundPage from "@/components/layouts/NotFoundPage";
import { IntegrationsSettings } from "@/pages/settings/pages/IntegrationsSettings";
import { IntegrationDetails } from "@/pages/settings/pages/IntegrationDetails";
import { NoteTakerSettings } from "@/pages/settings/pages/NoteTakerSettings";
import { SandboxBanner } from "@/components/layouts/SandboxBanner";
import { useIsSandboxAccount } from "@/services/auth/useIsSandboxAccount";
import { useIsNoteTakerAvailable } from "@/services/auth/useIsNoteTakerAvailable";

const Settings: React.FC = () => {
  const location = useLocation();
  const { enabled: showPartnersSettings } = useFeatureFlag(
    FeatureFlags.CRM_7805_PARTNERSHIPS
  );
  const { enabled: showIntegrationsSettings } = useFeatureFlag(
    FeatureFlags.CRM_9459_INTEGRATIONS_SETTINGS
  );
  const [currentPageName, setCurrentPageName] = useState(
    getSettingsPageNameFromPath()
  );
  const contentRef = useRef<HTMLDivElement>(null);
  const hasStoredReferrer = useRef(false);
  const [authRedirectProps, setShowAuthRedirect] =
    useState<AuthRedirectProps | null>(null);

  const handleAuthRedirectDone = () => {
    setShowAuthRedirect(null);
  };

  useEffect(() => {
    if (!hasStoredReferrer.current) {
      const state = location.state as { from?: string } | null;
      if (state?.from) {
        sessionStorage.setItem(SETTINGS_REFERRER_KEY, state.from);
      } else if (!sessionStorage.getItem(SETTINGS_REFERRER_KEY)) {
        // If no referrer is stored yet, default to home
        sessionStorage.setItem(SETTINGS_REFERRER_KEY, "/");
      }
      hasStoredReferrer.current = true;
    }
  }, [location.state]);

  useEffect(() => {
    setCurrentPageName(getSettingsPageNameFromPath());
  }, [location]);

  const accountData = useAccount().data;
  const userData = useUsersMe().data;
  const featureFlags = useFeatureFlags();
  const canAccessAdminSettings = useCanAccessAdminSettings();
  const isSandbox = useIsSandboxAccount();
  const isNoteTakerAvailable = useIsNoteTakerAvailable();

  const adminRoute = (element: React.ReactNode) =>
    canAccessAdminSettings ? (
      element
    ) : (
      <NotFoundPage data-test="settings-404" />
    );

  useEffect(() => {
    if (userData && accountData) {
      featureFlags.identify(userData, accountData);
    }
  }, [userData, accountData, featureFlags]);

  const onPageClick = (targetName: string) => {
    setCurrentPageName(targetName);
  };

  return (
    <>
      <StoreInitializer />
      {userData && accountData && (
        <>
          <div
            id="pendo-banner-settings"
            className="h-0 w-full bg-surface-elevation1 transition-all duration-300"
          ></div>
          <SandboxBanner />
          <div
            className={cn(
              "flex flex-row bg-surface-elevation0",
              isSandbox ? "h-[calc(100vh-36px)]" : "h-screen"
            )}
            data-test="settings-layout"
          >
            <SettingsSideBar
              data-test="settings-sidebar"
              currentPageName={currentPageName}
              onPageClick={(targetName: string) => onPageClick(targetName)}
            />
            <div
              data-test="settings-content-wrapper"
              className={cn(
                "flex-1 flex flex-col overflow-hidden min-h-0 bg-surface-sidebar-navigation"
              )}
            >
              <main
                data-test="settings-main-content"
                className="flex-1 m-2 bg-surface-elevation0 overflow-y-auto min-h-0 rounded-[20px] border-[0.5px] border-border-border1"
                ref={contentRef}
              >
                <Routes>
                  <Route
                    path="general"
                    element={adminRoute(<GeneralSettings />)}
                  />
                  <Route
                    path="productsLicenses"
                    element={adminRoute(<ProductsSettings />)}
                  />
                  <Route
                    path="productsLicenses/:productId"
                    element={adminRoute(<ProductsSettings />)}
                  />
                  <Route
                    path="userManagement"
                    element={adminRoute(<UserManagementSettings />)}
                  />
                  <Route path="deals" element={adminRoute(<DealsSettings />)} />
                  <Route
                    path="companies"
                    element={adminRoute(<CompaniesSettings />)}
                  />
                  <Route
                    path="people"
                    element={adminRoute(<PeopleSettings />)}
                  />
                  <Route path="leads" element={adminRoute(<LeadSettings />)} />
                  <Route
                    path="contracts"
                    element={adminRoute(<ContractsSettings />)}
                  />
                  <Route
                    path="contracts/stages/:stageId"
                    element={adminRoute(<OnboardingStageDetailedView />)}
                  />
                  <Route
                    path="contracts/stages/:stageId/:workflowId"
                    element={adminRoute(<OnboardingWorkflowDetailedView />)}
                  />
                  <Route
                    path="deals/stages/:stageId"
                    element={adminRoute(<StageDetailedView />)}
                  />
                  <Route
                    path="deals/stages/:stageId/:questionnaireId"
                    element={adminRoute(<WorkflowDetailedView />)}
                  />
                  {showPartnersSettings ? (
                    <Route
                      path="partners"
                      element={adminRoute(<PartnersSettings />)}
                    />
                  ) : null}
                  {showIntegrationsSettings ? (
                    <>
                      <Route
                        path="integrations"
                        element={adminRoute(
                          <IntegrationsSettings
                            onAuthRedirect={setShowAuthRedirect}
                          />
                        )}
                      />
                      <Route
                        path="integrations/:integrationId"
                        element={adminRoute(
                          <IntegrationDetails
                            onAuthRedirect={setShowAuthRedirect}
                          />
                        )}
                      />
                      {isNoteTakerAvailable && (
                        <Route
                          path="noteTaker"
                          element={adminRoute(<NoteTakerSettings />)}
                        />
                      )}
                    </>
                  ) : null}
                  <Route path="apiKeys" element={<PersonalApiKey />} />
                  {/* A sandbox can't connect email or calendar accounts. */}
                  {!isSandbox && (
                    <Route
                      path="connectedAccounts"
                      element={
                        <ConnectedAccounts
                          onAuthRedirect={setShowAuthRedirect}
                          hideIntegrations={
                            showIntegrationsSettings && canAccessAdminSettings
                          }
                        />
                      }
                    />
                  )}
                </Routes>
                {authRedirectProps && (
                  <AuthRedirect
                    {...authRedirectProps}
                    onDone={handleAuthRedirectDone}
                  />
                )}
                <Toaster />
              </main>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Settings;
